/**
 * MultiLineChartCard Component
 * Line chart with multiple series for comparing trends across items
 *
 * @description Used for per-title views over time in analytics dashboards
 */

import type { ReactElement } from "react";
import { AlertCircle, TrendingUp } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import type { ChartConfig } from "../ui/chart";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "../ui/chart";

// Distinct colors for chart lines (up to 5 titles)
const LINE_COLORS = [
  "hsl(43, 87%, 60%)", // Lumi accent (#F3B33B)
  "hsl(200, 80%, 60%)", // Blue
  "hsl(140, 70%, 50%)", // Green
  "hsl(280, 70%, 60%)", // Purple
  "hsl(20, 90%, 60%)", // Orange
];

interface TitleTrend {
  titleId: string;
  name: string;
  data: Array<{ date: string; views: number; watchTime: number }>;
}

interface MultiLineChartCardProps {
  readonly series: TitleTrend[];
  readonly title: string;
  readonly description?: string;
  readonly valueKey?: "views" | "watchTime";
  readonly footerMetric?: string;
  readonly footerValue?: string | number;
  readonly isLoading?: boolean;
  readonly error?: Error | null;
  readonly onRetry?: () => void;
}

/**
 * Props for skeleton and error sub-components
 */
interface ChartStateProps {
  readonly title: string;
  readonly description?: string;
}

interface ChartErrorProps extends ChartStateProps {
  readonly error: Error;
  readonly onRetry?: () => void;
}

// Static arrays for skeleton rendering (stable keys)
const Y_AXIS_ITEMS = ["y-1", "y-2", "y-3", "y-4", "y-5"] as const;
const X_AXIS_ITEMS = ["x-1", "x-2", "x-3", "x-4", "x-5", "x-6", "x-7"] as const;
const LEGEND_ITEMS = ["legend-1", "legend-2", "legend-3"] as const;

/**
 * Loading skeleton for the chart
 */
function ChartSkeleton({ title, description }: ChartStateProps): ReactElement {
  return (
    <Card className="border-neutral-800 bg-neutral-900/50">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-white">{title}</CardTitle>
        {description && <CardDescription className="text-neutral-400">{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="h-[300px] animate-pulse">
          {/* Y-axis skeleton */}
          <div className="flex h-full">
            <div className="w-8 flex flex-col justify-between py-4">
              {Y_AXIS_ITEMS.map((id) => (
                <div key={id} className="h-3 w-6 bg-neutral-800 rounded" />
              ))}
            </div>
            {/* Chart area skeleton */}
            <div className="flex-1 relative">
              {/* Grid lines */}
              <div className="absolute inset-0 flex flex-col justify-between py-4">
                {Y_AXIS_ITEMS.map((id) => (
                  <div key={`grid-${id}`} className="h-px bg-neutral-800" />
                ))}
              </div>
              {/* Fake line paths */}
              <div className="absolute inset-x-0 top-1/3 h-1 bg-neutral-800 rounded-full transform -rotate-3" />
              <div className="absolute inset-x-0 top-1/2 h-1 bg-neutral-700 rounded-full transform rotate-2" />
              <div className="absolute inset-x-0 top-2/3 h-1 bg-neutral-800 rounded-full transform -rotate-1" />
            </div>
          </div>
          {/* X-axis skeleton */}
          <div className="flex justify-between pt-2 px-8">
            {X_AXIS_ITEMS.map((id) => (
              <div key={id} className="h-3 w-10 bg-neutral-800 rounded" />
            ))}
          </div>
          {/* Legend skeleton */}
          <div className="flex justify-center gap-4 pt-4">
            {LEGEND_ITEMS.map((id) => (
              <div key={id} className="flex items-center gap-2">
                <div className="h-3 w-3 bg-neutral-700 rounded-full" />
                <div className="h-3 w-16 bg-neutral-800 rounded" />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Error state for the chart
 */
function ChartError({ title, description, error, onRetry }: ChartErrorProps): ReactElement {
  return (
    <Card className="border-neutral-800 bg-neutral-900/50">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-white">{title}</CardTitle>
        {description && <CardDescription className="text-neutral-400">{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center justify-center h-[300px] text-center space-y-4">
          <div className="p-3 bg-red-500/10 rounded-full">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <div className="space-y-1">
            <p className="text-neutral-300 font-medium">Failed to load chart data</p>
            <p className="text-sm text-neutral-500">{error.message}</p>
          </div>
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-4 py-2 text-sm font-medium text-black bg-lumi-accent rounded-lg hover:bg-lumi-accent/90 transition-colors"
            >
              Try Again
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Transform series data into chart-compatible format
 */
function buildChartData(series: TitleTrend[], valueKey: "views" | "watchTime"): Array<Record<string, unknown>> {
  // Merge all dates from all series
  const allDates = new Set<string>();
  for (const s of series) {
    for (const d of s.data) {
      allDates.add(d.date);
    }
  }

  // Sort dates chronologically
  const sortedDates = Array.from(allDates).sort((a, b) => a.localeCompare(b));

  // Build chart data with a column for each title
  return sortedDates.map((date) => {
    const point: Record<string, unknown> = {
      name: new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    };
    for (const s of series) {
      const dataPoint = s.data.find((d) => d.date === date);
      point[s.titleId] = dataPoint ? dataPoint[valueKey] : 0;
    }
    return point;
  });
}

/**
 * Build chart config for each series
 */
function buildChartConfig(series: TitleTrend[]): ChartConfig {
  const config: ChartConfig = {};
  for (let i = 0; i < series.length; i++) {
    const s = series[i];
    config[s.titleId] = {
      label: s.name,
      color: LINE_COLORS[i % LINE_COLORS.length],
    };
  }
  return config;
}

/**
 * Empty state component
 */
function ChartEmpty({ title, description }: ChartStateProps): ReactElement {
  return (
    <Card className="border-neutral-800 bg-neutral-900/50">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-white">{title}</CardTitle>
        {description && <CardDescription className="text-sm text-neutral-400">{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-center h-[300px] text-neutral-500">No data available</div>
      </CardContent>
    </Card>
  );
}

export function MultiLineChartCard({
  series,
  title,
  description,
  valueKey = "views",
  footerMetric,
  footerValue,
  isLoading = false,
  error = null,
  onRetry,
}: MultiLineChartCardProps): ReactElement {
  // Handle loading state
  if (isLoading) {
    return <ChartSkeleton title={title} description={description} />;
  }

  // Handle error state
  if (error) {
    return <ChartError title={title} description={description} error={error} onRetry={onRetry} />;
  }

  // Handle empty data
  if (series.length === 0) {
    return <ChartEmpty title={title} description={description} />;
  }

  const chartData = buildChartData(series, valueKey);
  const chartConfig = buildChartConfig(series);

  // Calculate totals
  const totals = series.reduce((sum, s) => sum + s.data.reduce((acc, d) => acc + d[valueKey], 0), 0);

  // Legend formatter - stable reference
  const legendFormatter = (value: string): ReactElement => {
    const config = chartConfig[value];
    return <span className="text-neutral-300 text-xs">{config?.label ?? value}</span>;
  };

  return (
    <Card className="border-neutral-800 bg-transparent">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-white">{title}</CardTitle>
            {description && <CardDescription className="text-sm text-neutral-400">{description}</CardDescription>}
          </div>
          {(footerMetric || footerValue) && (
            <div className="flex flex-col items-end gap-1 text-sm pt-2">
              <div className="flex gap-2 font-medium leading-none text-white">
                {footerMetric}: {typeof footerValue === "number" ? footerValue.toLocaleString() : footerValue}
                <TrendingUp className="h-4 w-4 text-lumi-accent" />
              </div>
              <div className="leading-none text-neutral-500">
                Combined: {totals.toLocaleString()} {valueKey === "views" ? "views" : "min"}
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-0 -ml-4 pr-4">
        <ChartContainer config={chartConfig} className="h-[300px] w-full !aspect-auto">
          <LineChart data={chartData} margin={{ top: 20, right: 12, left: 12, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-neutral-800" />
            <XAxis dataKey="name" tickLine={false} tickMargin={10} axisLine={false} className="text-neutral-500" />
            <YAxis tickLine={false} axisLine={false} tickMargin={8} className="text-neutral-500" />
            <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" />} />
            <Legend verticalAlign="bottom" height={36} formatter={legendFormatter} className="mt-8" />
            {series.map((s, i) => (
              <Line
                key={s.titleId}
                type="monotone"
                dataKey={s.titleId}
                name={s.name}
                stroke={LINE_COLORS[i % LINE_COLORS.length]}
                strokeWidth={2}
                dot={{ fill: LINE_COLORS[i % LINE_COLORS.length], r: 3 }}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
